/**
 * translateScannerNote
 *
 * The Cookie-Radar scanner always emits security-header notes in German.
 * This utility translates those known strings to English when the UI language
 * is English. All unknown strings fall back to the original German so that
 * the scanner output is never silently lost.
 *
 * To add support for a new note emitted by the scanner, add an entry to
 * EXACT_TRANSLATIONS (for static strings) or REGEX_TRANSLATIONS (for strings
 * that contain variable values, e.g. the actual header value).
 *
 * The source of truth for all possible strings is:
 *   cookie-radar-customized/cookiebot/security_headers.py
 */

// ── Static (exact-match) translations ────────────────────────────────────────

const EXACT: Record<string, string> = {
  // Strict-Transport-Security
  'Header fehlt – HTTPS wird nicht erzwungen.':
    'Header missing – HTTPS is not enforced.',
  'Kein gültiger max-age-Wert gefunden.':
    'No valid max-age value found.',
  'max-age=0 deaktiviert HSTS effektiv.':
    'max-age=0 effectively disables HSTS.',
  'includeSubDomains fehlt – Subdomains sind nicht geschützt.':
    'includeSubDomains missing – subdomains are not protected.',
  'preload-Direktive fehlt – Site kann nicht in Browser-HSTS-Preload-Listen eingetragen werden.':
    'preload directive missing – site cannot be added to browser HSTS preload lists.',

  // Content-Security-Policy
  'Header fehlt – kein Schutz vor Cross-Site-Scripting (XSS).':
    'Header missing – no protection against Cross-Site Scripting (XSS).',
  "'unsafe-inline' erlaubt inline-Scripts – XSS-Schutz eingeschränkt.":
    "'unsafe-inline' allows inline scripts – XSS protection is reduced.",
  "'unsafe-eval' erlaubt eval() – erhöhtes XSS-Risiko.":
    "'unsafe-eval' allows eval() – increased XSS risk.",
  'Wildcard (*) in script-src oder default-src – zu permissiv.':
    'Wildcard (*) in script-src or default-src – too permissive.',
  'Keine offensichtlichen Sicherheitsprobleme gefunden.':
    'No obvious security issues found.',

  // X-Frame-Options
  'Header fehlt – Clickjacking möglich.':
    'Header missing – clickjacking is possible.',
  'ALLOW-FROM ist veraltet und wird von modernen Browsern ignoriert.':
    'ALLOW-FROM is deprecated and ignored by modern browsers.',

  // X-Content-Type-Options
  'Header fehlt – MIME-Sniffing-Angriffe möglich.':
    'Header missing – MIME sniffing attacks are possible.',

  // Referrer-Policy
  'Header fehlt – Browser verwendet Standard (no-referrer-when-downgrade), dabei werden URLs an externe Seiten übermittelt.':
    'Header missing – browser uses default (no-referrer-when-downgrade), which sends full URLs to external sites.',

  // Permissions-Policy
  'Header fehlt – Browser-Features (Kamera, Mikrofon, etc.) werden nicht eingeschränkt.':
    'Header missing – browser features (camera, microphone, etc.) are not restricted.',
};

// ── Regex (pattern-match) translations for strings with interpolated values ──

type RegexTranslation = {
  pattern: RegExp;
  translate: (match: RegExpMatchArray) => string;
};

const REGEX: RegexTranslation[] = [
  {
    // "max-age=12345 ist zu kurz (empfohlen: ≥ 31.536.000 s / 1 Jahr)."
    pattern: /^max-age=(\d+) ist zu kurz \(empfohlen: ≥ 31\.536\.000 s \/ 1 Jahr\)\.$/,
    translate: (m) =>
      `max-age=${m[1]} is too short (recommended: ≥ 31,536,000 s / 1 year).`,
  },
  {
    // "'same-origin' kann Referrer-Informationen an Dritte weitergeben."
    pattern: /^'(.+)' kann Referrer-Informationen an Dritte weitergeben\.$/,
    translate: (m) =>
      `'${m[1]}' may share referrer information with third parties.`,
  },
  {
    // "'unsafe-url' gibt vollständige URLs als Referrer weiter – datenschutzrelevant."
    pattern: /^'(.+)' gibt vollständige URLs als Referrer weiter – datenschutzrelevant\.$/,
    translate: (m) =>
      `'${m[1]}' sends full URLs as referrer – privacy-relevant.`,
  },
  {
    // "Unbekannter Wert 'xyz' – erwartet: nosniff."
    pattern: /^Unbekannter Wert '(.+)' – erwartet: nosniff\.$/,
    translate: (m) =>
      `Unknown value '${m[1]}' – expected: nosniff.`,
  },
  {
    // "Unbekannter Wert: xyz"  (X-Frame-Options catch-all)
    pattern: /^Unbekannter Wert: (.+)$/,
    translate: (m) =>
      `Unknown value: ${m[1]}`,
  },
];

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Translates a scanner-emitted security-header note to English when
 * `lang` starts with "en". Returns the original string for all other
 * languages or for any string not found in the translation tables.
 */
export function translateScannerNote(note: string, lang: string): string {
  if (!note || !lang.startsWith('en')) return note;

  // 1. Exact match
  const exact = EXACT[note];
  if (exact !== undefined) return exact;

  // 2. Regex match (for strings containing interpolated values)
  for (const { pattern, translate } of REGEX) {
    const m = note.match(pattern);
    if (m) return translate(m);
  }

  // 3. Unknown string – return as-is (safe fallback)
  return note;
}
